import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Canvas, createPortal, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import {
  Environment,
  Line,
  Text,
  useGLTF,
  useTexture,
} from "@react-three/drei";
import {
  XR,
  Controllers,
  Interactive,
  startSession,
  useController,
  useXREvent,
  useXR,
} from "@react-three/xr";
import { colorNamesExterior } from "../UI/Customizer/util/colorNames";
import hdr from "../../assets/images/Refine4.hdr";
import xrNavigationBoard from "../../assets/images/xr-navigation-board.png";
import useCustomizerStore from "../../state/customizer";
import useTransitionStore from "../../state/transitions";
import {
  fetchCustomizerData,
  getSelectedColorIndex,
  getSelectedWheelIndex,
  setSelectedColorIndex,
  setSelectedWheelIndex,
} from "../../util/customizerData";
import {
  getPreferredImmersiveSessionMode,
  getXrEnterLabel,
} from "../../util/xrSessionSupport";
import TunnelContainer from "../webgl/Car/Tunnel/TunnelContainer";
import DayXrEnvironment from "../webgl/DayXrEnvironment";
import ToyotaCrownShowroomModel from "./ToyotaCrownShowroomModel";
import useFogControlStore from "../../state/fogControls";
import Lights from "../webgl/Car/CarFlip/Lights";
const SHOWROOM_ENVIRONMENT_X_OFFSET = 0;
const SHOWROOM_ENVIRONMENT_Z_OFFSET = 0;
const SHOWROOM_FLOOR_OFFSET = 0;
const SHOWROOM_ENVIRONMENT_ROTATION = Math.PI / 2;
const SHOWROOM_XR_SPAWN_POSITION = [-2.2, 0, 1.4];
const SHOWROOM_XR_LOOK_TARGET = [0, 1.15, 0];
const XR_INSTRUCTION_BOARD_POSITION = [3.1, 1.72, -3.35];
const XR_INSTRUCTION_BOARD_SCALE = [3.2, 1.8, 1];
const XR_INSTRUCTION_BOARD_ROTATION_Y = (-38 * Math.PI) / 180;
const XR_MENU_WIDTH = 1.54;
const XR_MENU_TOP = 0.74;
const XR_MENU_BOTTOM_PADDING = 0.21;
const XR_SWATCH_ROW_GAP = 0.31;
const XR_WHEEL_ROW_GAP = 0.26;
const XR_TRIM_TRAY_X = -1.26;
const XR_TRIM_TRAY_Z = 0.12;
const XR_TRIM_TRAY_WIDTH = 0.54;
const XR_TRIM_TRAY_HEIGHT = 0.94;
const XR_TRIM_TRAY_TOP_Y = 0.28;
const XR_TRIM_TRAY_GAP = 0.28;
const XR_TRIM_NOTCH_CLOSED_X = 0.35;
const XR_TRIM_NOTCH_OPEN_X = -0.35;
const XR_TRIM_NOTCH_WIDTH = 0.16;
const XR_TRIM_NOTCH_HEIGHT = 0.4;
const SWATCH_OUTER_GEOMETRY = new THREE.CircleGeometry(0.14, 40);
const SWATCH_INNER_RING_GEOMETRY = new THREE.CircleGeometry(0.12, 40);
const SWATCH_FILL_GEOMETRY = new THREE.CircleGeometry(0.103, 40);
const SWATCH_HALF_FILL_GEOMETRY = new THREE.CircleGeometry(
  0.103,
  40,
  -Math.PI / 2,
  Math.PI
);
const SWATCH_WHITE_EDGE_GEOMETRY = new THREE.RingGeometry(0.095, 0.103, 40);
const SWATCH_FILL_SCALE = 1.15;

export default function ShowroomCanvas({
  onBack,
  trimLabel,
  backLabel = "Back To Trims",
  titleOverride,
  descriptionOverride,
  hideOverlay = false,
  onCanvasReady,
  renderSceneContent = true,
}) {
  const [vrPending, setVrPending] = useState(false);
  const [showUnsupportedPopup, setShowUnsupportedPopup] = useState(false);
  const [sessionMode, setSessionMode] = useState(null);
  const [sceneEpoch, setSceneEpoch] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const checkVrSupport = async () => {
      const supportedMode = await getPreferredImmersiveSessionMode(navigator?.xr);

      if (!cancelled) {
        setSessionMode(supportedMode);
      }
    };

    checkVrSupport();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleEnterVr = async () => {
    if (vrPending) {
      return;
    }

    try {
      const resolvedMode =
        sessionMode ??
        (await getPreferredImmersiveSessionMode(navigator?.xr));

      if (!resolvedMode) {
        setShowUnsupportedPopup(true);
        return;
      }

      setSessionMode(resolvedMode);
      setVrPending(true);

      const session = await startSession(resolvedMode, {
        optionalFeatures: ["local-floor", "bounded-floor", "hand-tracking"],
      });

      if (session) {
        session.addEventListener(
          "end",
          () => {
            setVrPending(false);
            setSceneEpoch((current) => current + 1);
          },
          { once: true }
        );
      } else {
        setVrPending(false);
        setShowUnsupportedPopup(true);
      }
    } catch (error) {
      setVrPending(false);
      setShowUnsupportedPopup(true);
      console.error("Unable to start immersive VR session", error);
    }
  };

  return (
    <StageRoot>
      <Canvas
        key={sceneEpoch}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true }}
        camera={{ position: [0, 1.65, 7.2], fov: 32, near: 0.1, far: 320 }}
        style={{ position: "absolute", inset: 0 }}
        onCreated={() => {
          onCanvasReady?.();
        }}
      >
        <XR referenceSpace="local-floor">
          {renderSceneContent ? <ShowroomScene /> : null}
        </XR>
      </Canvas>

      {!hideOverlay && (
        <Overlay>
          <div className="meta">
            <button type="button" className="ghost" onClick={onBack}>
              {backLabel}
            </button>
            <div className="copy">
              <span className="eyebrow">Showroom</span>
              <h2>{titleOverride ?? `${trimLabel.toUpperCase()} in WebXR`}</h2>
              <p>
                {descriptionOverride ??
                  "Quest uses controller-mounted controls. Apple Vision Pro and other controllerless devices use a fixed world menu with direct selection."}
              </p>
            </div>
          </div>

          {sessionMode && (
            <div className="actions">
              <button
                type="button"
                className="primary xr-button"
                onClick={handleEnterVr}
                disabled={vrPending}
              >
                {vrPending ? "Entering XR..." : getXrEnterLabel(sessionMode)}
              </button>
            </div>
          )}
        </Overlay>
      )}

      {showUnsupportedPopup && (
        <UnsupportedVrModal role="dialog" aria-modal="true">
          <div
            className="backdrop"
            onClick={() => setShowUnsupportedPopup(false)}
          />
          <div className="panel">
            <span className="eyebrow">WebXR Required</span>
            <h3>Open This On A Supported Device</h3>
            <p>
              This browser cannot start an immersive WebXR session. Quest uses
              immersive VR. Apple Vision Pro support depends on the browser
              exposing immersive WebXR and natural input.
            </p>
            <button
              type="button"
              className="dismiss"
              onClick={() => setShowUnsupportedPopup(false)}
            >
              Continue On Screen
            </button>
          </div>
        </UnsupportedVrModal>
      )}
    </StageRoot>
  );
}

function ShowroomScene() {
  const dayFog = useFogControlStore((state) => state.dayFog);
  const xrCarPosition = useFogControlStore((state) => state.xrCarPosition);
  const isPresenting = useXR((state) => state.isPresenting);
  const leftController = useController("left");
  const rightController = useController("right");
  const naturalInputMode = isPresenting && !leftController && !rightController;
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <XrSpawnAnchor />
      <color attach="background" args={["#000000"]} />
      {dayFog.enabled && (
        <fog attach="fog" args={[dayFog.color, dayFog.near, dayFog.far]} />
      )}
      <Controllers
        rayMaterial={{ color: "#f5f3ef", transparent: true, opacity: 0 }}
      />

      <Suspense fallback={null}>
        <DayXrEnvironment radius={220} mode="day" />
        <TunnelContainer
          floorOffset={SHOWROOM_FLOOR_OFFSET}
          xOffset={SHOWROOM_ENVIRONMENT_X_OFFSET}
          zOffset={SHOWROOM_ENVIRONMENT_Z_OFFSET}
          rotationY={SHOWROOM_ENVIRONMENT_ROTATION}
          visibleOverride
          forceVisible
        />
        <XrInstructionBoard />
        <ToyotaCrownShowroomModel
          xOffset={xrCarPosition.x}
          floorOffset={xrCarPosition.y}
          depthOffset={xrCarPosition.z}
        />
        <Lights />
        {naturalInputMode ? (
          <>
            <NaturalInputTeleportPlane />
            <WorldAnchoredColorMenu />
          </>
        ) : (
          <>
            <RightGripTeleportationPlane />
            <RightControllerMenuRay visible={menuOpen} />
            <LeftHandColorMenu open={menuOpen} onOpenChange={setMenuOpen} />
          </>
        )}
      </Suspense>
    </>
  );
}

function XrSpawnAnchor() {
  const player = useXR((state) => state.player);
  const isPresenting = useXR((state) => state.isPresenting);

  useEffect(() => {
    if (!isPresenting || !player) {
      return;
    }

    player.position.set(...SHOWROOM_XR_SPAWN_POSITION);
    const target = new THREE.Vector3(...SHOWROOM_XR_LOOK_TARGET);
    const direction = target.sub(player.position);
    const yaw = Math.atan2(direction.x, direction.z);
    player.rotation.set(0, yaw, 0);
    player.updateMatrixWorld();
  }, [isPresenting, player]);

  return null;
}

function XrInstructionBoard() {
  const boardTexture = useTexture(xrNavigationBoard);

  useEffect(() => {
    boardTexture.encoding = THREE.sRGBEncoding;
    boardTexture.anisotropy = 8;
    boardTexture.needsUpdate = true;
  }, [boardTexture]);

  return (
    <group
      position={[
        SHOWROOM_ENVIRONMENT_X_OFFSET,
        SHOWROOM_FLOOR_OFFSET,
        SHOWROOM_ENVIRONMENT_Z_OFFSET,
      ]}
      rotation={[0, SHOWROOM_ENVIRONMENT_ROTATION, 0]}
    >
      <mesh
        position={XR_INSTRUCTION_BOARD_POSITION}
        rotation={[0, XR_INSTRUCTION_BOARD_ROTATION_Y, 0]}
        scale={XR_INSTRUCTION_BOARD_SCALE}
        renderOrder={8}
      >
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial
          map={boardTexture}
          toneMapped={false}
          side={THREE.DoubleSide}
        />
      </mesh>
    </group>
  );
}

function RightGripTeleportationPlane() {
  const rightController = useController("right");
  const player = useXR((state) => state.player);
  const marker = useRef(null);
  const markerCore = useRef(null);
  const markerPulse = useRef(null);
  const intersection = useRef(null);
  const camera = useThree((state) => state.camera);
  const { nodes } = useGLTF("/models/tunnel.glb");
  const [teleportArmed, setTeleportArmed] = useState(false);
  const [lassoPoints, setLassoPoints] = useState(null);

  const allowRightGrip = (event) =>
    event.target.inputSource?.handedness === "right";

  const getViewerWorldPosition = () => {
    const viewerWorldPosition = new THREE.Vector3();
    camera.getWorldPosition(viewerWorldPosition);
    return viewerWorldPosition;
  };

  const getDistanceToViewer = (point) => {
    const viewerWorldPosition = getViewerWorldPosition();
    return point.distanceTo(viewerWorldPosition);
  };

  const snapViewerToPoint = (point) => {
    if (!player) {
      return;
    }

    const viewerWorldPosition = getViewerWorldPosition();
    player.position.x += point.x - viewerWorldPosition.x;
    player.position.z += point.z - viewerWorldPosition.z;
    player.updateMatrixWorld(true);
  };

  const updateMarker = (point, distance) => {
    if (!marker.current) {
      return;
    }

    const isVisible = distance <= 14;
    marker.current.visible = isVisible;
    marker.current.position.copy(point);
    if (markerCore.current) {
      markerCore.current.visible = isVisible;
      markerCore.current.position.copy(point);
    }
    if (markerPulse.current) {
      markerPulse.current.visible = isVisible;
      markerPulse.current.position.copy(point);
    }
  };

  const hideLasso = () => {
    setLassoPoints(null);
  };

  const hideTeleportVisuals = () => {
    if (marker.current) {
      marker.current.visible = false;
    }
    if (markerCore.current) {
      markerCore.current.visible = false;
    }
    if (markerPulse.current) {
      markerPulse.current.visible = false;
    }
    hideLasso();
  };

  const updateLasso = (point) => {
    if (!rightController?.controller) {
      return;
    }

    const start = new THREE.Vector3();
    const end = point.clone();
    const mid = new THREE.Vector3();
    const curve = new THREE.QuadraticBezierCurve3();
    rightController.controller.getWorldPosition(start);
    mid.copy(start).lerp(end, 0.5);
    mid.y += Math.max(0.8, start.distanceTo(end) * 0.18);
    curve.v0.copy(start);
    curve.v1.copy(mid);
    curve.v2.copy(end);
    setLassoPoints(curve.getPoints(28).map((vector) => vector.toArray()));
  };

  useFrame(({ clock }) => {
    const pulse = (Math.sin(clock.elapsedTime * 4.2) + 1) / 2;

    if (marker.current?.visible) {
      marker.current.scale.setScalar(1 + pulse * 0.12);
    }

    if (markerCore.current?.visible) {
      markerCore.current.scale.setScalar(0.94 + pulse * 0.06);
      markerCore.current.material.opacity = 0.28 + pulse * 0.22;
    }

    if (markerPulse.current?.visible) {
      markerPulse.current.scale.setScalar(0.88 + pulse * 0.55);
      markerPulse.current.material.opacity = 0.08 + (1 - pulse) * 0.18;
    }

    if (!intersection.current || !marker.current?.visible || !teleportArmed) {
      hideLasso();
    }
  });

  useXREvent(
    "squeezestart",
    () => {
      setTeleportArmed(true);
    },
    { handedness: "right" }
  );

  useXREvent(
    "squeezeend",
    () => {
      setTeleportArmed(false);
      intersection.current = null;
      hideTeleportVisuals();
    },
    { handedness: "right" }
  );

  return (
    <group position={[0, 0.002, 0]}>
      <mesh ref={marker} visible={false} rotation-x={-Math.PI / 2}>
        <ringGeometry args={[0.15, 0.28, 72]} />
        <meshBasicMaterial color="#e20500" transparent opacity={0.94} />
      </mesh>
      <mesh ref={markerCore} visible={false} rotation-x={-Math.PI / 2}>
        <circleGeometry args={[0.08, 52]} />
        <meshBasicMaterial color="#e20500" transparent opacity={0.42} />
      </mesh>
      <mesh ref={markerPulse} visible={false} rotation-x={-Math.PI / 2}>
        <ringGeometry args={[0.29, 0.33, 72]} />
        <meshBasicMaterial color="#e20500" transparent opacity={0.18} />
      </mesh>
      {lassoPoints && (
        <Line
          points={lassoPoints}
          color="#e20500"
          lineWidth={5}
          transparent
          opacity={0.98}
          depthTest={false}
          renderOrder={50}
          frustumCulled={false}
        />
      )}

      <Interactive
        onMove={(event) => {
          if (!teleportArmed || !allowRightGrip(event) || !event.intersection) {
            return;
          }

          intersection.current = event.intersection.point;
          updateLasso(event.intersection.point);
          updateMarker(event.intersection.point, getDistanceToViewer(event.intersection.point));
        }}
        onHover={(event) => {
          if (
            !teleportArmed ||
            !allowRightGrip(event) ||
            !event.intersection ||
            !marker.current
          ) {
            return;
          }

          updateMarker(event.intersection.point, getDistanceToViewer(event.intersection.point));
          updateLasso(event.intersection.point);
          marker.current.scale.setScalar(1);
        }}
        onBlur={(event) => {
          if (!allowRightGrip(event) || !marker.current) {
            return;
          }

          hideTeleportVisuals();
          intersection.current = null;
        }}
        onSelectStart={(event) => {
          if (
            !teleportArmed ||
            !allowRightGrip(event) ||
            !event.intersection ||
            !marker.current
          ) {
            return;
          }

          intersection.current = event.intersection.point;
          updateLasso(event.intersection.point);
          updateMarker(event.intersection.point, getDistanceToViewer(event.intersection.point));
          marker.current.scale.setScalar(1.14);
          if (markerPulse.current) {
            markerPulse.current.scale.setScalar(1.04);
          }
        }}
        onSelectEnd={(event) => {
          if (
            !teleportArmed ||
            !allowRightGrip(event) ||
            !marker.current ||
            !intersection.current
          ) {
            return;
          }

          const distance = getDistanceToViewer(intersection.current);
          marker.current.scale.setScalar(1);
          if (markerCore.current) {
            markerCore.current.scale.setScalar(1);
          }
          if (markerPulse.current) {
            markerPulse.current.scale.setScalar(1);
          }

          if (distance <= 14) {
            snapViewerToPoint(intersection.current);
            marker.current.visible = true;
            if (markerCore.current) {
              markerCore.current.visible = true;
            }
            if (markerPulse.current) {
              markerPulse.current.visible = true;
            }
            updateLasso(intersection.current);
          }
        }}
      >
        <group
          position={[
            SHOWROOM_ENVIRONMENT_X_OFFSET,
            SHOWROOM_FLOOR_OFFSET,
            SHOWROOM_ENVIRONMENT_Z_OFFSET,
          ]}
          rotation={[0, SHOWROOM_ENVIRONMENT_ROTATION, 0]}
        >
          <mesh geometry={nodes.shadowPlane.geometry}>
            <meshBasicMaterial
              transparent
              opacity={0}
              depthWrite={false}
              side={2}
            />
          </mesh>
        </group>
      </Interactive>
    </group>
  );
}

function RightControllerMenuRay({ visible }) {
  const rightController = useController("right");

  if (!rightController?.controller) {
    return null;
  }

  return createPortal(
    <group visible={visible} position={[0, -0.005, -0.04]}>
      <Line
        points={[
          [0, 0, 0],
          [0, 0, -0.18],
        ]}
        color="#f5f3ef"
        lineWidth={2.2}
        transparent
        opacity={0.58}
        depthTest={false}
        renderOrder={40}
      />
      <Line
        points={[
          [0, 0, -0.18],
          [0, 0, -0.36],
        ]}
        color="#f5f3ef"
        lineWidth={1.1}
        transparent
        opacity={0.14}
        depthTest={false}
        renderOrder={40}
      />
    </group>,
    rightController.controller
  );
}

function NaturalInputTeleportPlane() {
  const player = useXR((state) => state.player);
  const camera = useThree((state) => state.camera);
  const { nodes } = useGLTF("/models/tunnel.glb");
  const marker = useRef(null);
  const markerCore = useRef(null);
  const markerPulse = useRef(null);
  const targetPoint = useRef(null);

  const getViewerWorldPosition = () => {
    const viewerWorldPosition = new THREE.Vector3();
    camera.getWorldPosition(viewerWorldPosition);
    return viewerWorldPosition;
  };

  const snapViewerToPoint = (point) => {
    if (!player) {
      return;
    }

    const viewerWorldPosition = getViewerWorldPosition();
    player.position.x += point.x - viewerWorldPosition.x;
    player.position.z += point.z - viewerWorldPosition.z;
    player.updateMatrixWorld(true);
  };

  const updateMarker = (point) => {
    [marker.current, markerCore.current, markerPulse.current].forEach((mesh) => {
      if (!mesh) {
        return;
      }

      mesh.visible = true;
      mesh.position.copy(point);
    });
  };

  const hideMarker = () => {
    [marker.current, markerCore.current, markerPulse.current].forEach((mesh) => {
      if (mesh) {
        mesh.visible = false;
      }
    });
  };

  useFrame(({ clock }) => {
    const pulse = (Math.sin(clock.elapsedTime * 4.2) + 1) / 2;

    if (marker.current?.visible) {
      marker.current.scale.setScalar(1 + pulse * 0.12);
    }

    if (markerCore.current?.visible) {
      markerCore.current.scale.setScalar(0.94 + pulse * 0.06);
      markerCore.current.material.opacity = 0.28 + pulse * 0.22;
    }

    if (markerPulse.current?.visible) {
      markerPulse.current.scale.setScalar(0.88 + pulse * 0.55);
      markerPulse.current.material.opacity = 0.08 + (1 - pulse) * 0.18;
    }
  });

  return (
    <group position={[0, 0.002, 0]}>
      <mesh ref={marker} visible={false} rotation-x={-Math.PI / 2}>
        <ringGeometry args={[0.15, 0.28, 72]} />
        <meshBasicMaterial color="#e20500" transparent opacity={0.94} />
      </mesh>
      <mesh ref={markerCore} visible={false} rotation-x={-Math.PI / 2}>
        <circleGeometry args={[0.08, 52]} />
        <meshBasicMaterial color="#e20500" transparent opacity={0.42} />
      </mesh>
      <mesh ref={markerPulse} visible={false} rotation-x={-Math.PI / 2}>
        <ringGeometry args={[0.29, 0.33, 72]} />
        <meshBasicMaterial color="#e20500" transparent opacity={0.18} />
      </mesh>

      <Interactive
        onMove={(event) => {
          if (!event.intersection) {
            return;
          }

          targetPoint.current = event.intersection.point;
          updateMarker(event.intersection.point);
        }}
        onHover={(event) => {
          if (!event.intersection) {
            return;
          }

          targetPoint.current = event.intersection.point;
          updateMarker(event.intersection.point);
        }}
        onBlur={() => {
          targetPoint.current = null;
          hideMarker();
        }}
        onSelect={(event) => {
          const point = event.intersection?.point ?? targetPoint.current;

          if (!point) {
            return;
          }

          snapViewerToPoint(point);
          updateMarker(point);
        }}
      >
        <group
          position={[
            SHOWROOM_ENVIRONMENT_X_OFFSET,
            SHOWROOM_FLOOR_OFFSET,
            SHOWROOM_ENVIRONMENT_Z_OFFSET,
          ]}
          rotation={[0, SHOWROOM_ENVIRONMENT_ROTATION, 0]}
        >
          <mesh geometry={nodes.shadowPlane.geometry}>
            <meshBasicMaterial
              transparent
              opacity={0}
              depthWrite={false}
              side={2}
            />
          </mesh>
        </group>
      </Interactive>
    </group>
  );
}

function WorldAnchoredColorMenu() {
  const model = useCustomizerStore((state) => state.model);
  const setModel = useCustomizerStore((state) => state.setModel);
  const setTrimColor = useCustomizerStore((state) => state.setTrimColor);
  const selectionByModel = useCustomizerStore((state) => state.selectionByModel);
  const setWheelIndex = useTransitionStore((state) => state.setWheelIndex);
  const trims = useMemo(() => fetchCustomizerData(), []);
  const trim = trims[model];
  const colors = trim.exterior.colors;
  const wheelOptions = trim.wheels.colors ?? [];
  const wheelLabels = trim.wheels.text ?? [];
  const label = trim.label.toUpperCase();
  const hasWheelOptions = wheelOptions.length > 0;
  const colorRows = Math.ceil(colors.length / 3);
  const selectedColorY = XR_MENU_TOP - 0.17;
  const trimLabelY = selectedColorY - 0.14;
  const firstSwatchY = trimLabelY - 0.31;
  const lastSwatchY =
    firstSwatchY - Math.max(0, colorRows - 1) * XR_SWATCH_ROW_GAP;
  const firstWheelY = lastSwatchY - 0.28;
  const lastWheelY =
    firstWheelY - Math.max(0, wheelOptions.length - 1) * XR_WHEEL_ROW_GAP;
  const contentBottomY = hasWheelOptions ? lastWheelY - 0.11 : lastSwatchY - 0.14;
  const menuBottom = contentBottomY - XR_MENU_BOTTOM_PADDING;
  const menuHeight = XR_MENU_TOP - menuBottom;
  const menuCenterY = (XR_MENU_TOP + menuBottom) / 2;
  const selectedIndex = selectionByModel?.[model]?.exterior ?? 0;
  const selectedWheel = selectionByModel?.[model]?.wheel ?? 0;
  const selectedColorName =
    colorNamesExterior[selectedIndex]?.toUpperCase() ?? "";

  useEffect(() => {
    setWheelIndex(selectedWheel);
  }, [selectedWheel, setWheelIndex]);

  const handleColorSelect = (color, index) => {
    setSelectedColorIndex(model, index, "exterior");
    setTrimColor(color, model);
  };

  const handleWheelSelect = (index) => {
    setSelectedWheelIndex(model, index);
    setWheelIndex(index);
  };

  const handleTrimSelect = (nextModel) => {
    if (nextModel === model) {
      return;
    }

    setModel(nextModel);
    setWheelIndex(getSelectedWheelIndex(nextModel));
  };

  return (
    <group position={[-1.35, 1.15, -0.72]} rotation={[0, 0.45, 0]} scale={0.33}>
      <mesh position={[XR_TRIM_TRAY_X + 0.18, 0.08, 0.01]}>
        <planeGeometry args={[XR_TRIM_TRAY_WIDTH, XR_TRIM_TRAY_HEIGHT]} />
        <meshBasicMaterial color="#0e1114" transparent opacity={0.94} />
      </mesh>
      {trims.map((trimOption, index) => (
        <TrimMenuOption
          key={`world-trim-selector-${trimOption.label}`}
          label={trimOption.label}
          isSelected={index === model}
          position={[
            XR_TRIM_TRAY_X + 0.18,
            XR_TRIM_TRAY_TOP_Y - index * XR_TRIM_TRAY_GAP + 0.08,
            0.03,
          ]}
          onSelect={() => handleTrimSelect(index)}
        />
      ))}

      <mesh position={[0, menuCenterY + 0.01, -0.01]}>
        <planeGeometry args={[XR_MENU_WIDTH, menuHeight]} />
        <meshBasicMaterial color="#0e1114" transparent opacity={0.92} />
      </mesh>

      <Text
        position={[0, selectedColorY, 0]}
        fontSize={0.08}
        maxWidth={1.18}
        color="#f5f3ef"
        anchorX="center"
        anchorY="middle"
      >
        {selectedColorName}
      </Text>
      <Text
        position={[0, trimLabelY, 0]}
        fontSize={0.05}
        maxWidth={1.18}
        color="#c7cbd1"
        anchorX="center"
        anchorY="middle"
      >
        {`${label} COLORS`}
      </Text>

      {colors.map((color, index) => {
        const column = index % 3;
        const row = Math.floor(index / 3);

        return (
          <ColorMenuSwatch
            key={`${label}-world-color-${index}`}
            color={color}
            isSelected={selectedIndex === index}
            position={[
              -0.42 + column * 0.42,
              firstSwatchY - row * XR_SWATCH_ROW_GAP,
              0,
            ]}
            onSelect={() => handleColorSelect(color, index)}
          />
        );
      })}

      {hasWheelOptions &&
        wheelOptions.map((wheel, index) => (
          <WheelMenuOption
            key={`${label}-world-wheel-${index}`}
            index={index}
            isSelected={selectedWheel === index}
            label={wheelLabels[index] ?? `Option ${index + 1}`}
            wheelSrc={wheel}
            onSelect={() => handleWheelSelect(index)}
            position={[0, firstWheelY - index * XR_WHEEL_ROW_GAP, 0]}
          />
        ))}
    </group>
  );
}

function LeftHandColorMenu({ open, onOpenChange }) {
  const leftController = useController("left");
  const model = useCustomizerStore((state) => state.model);
  const setModel = useCustomizerStore((state) => state.setModel);
  const setTrimColor = useCustomizerStore((state) => state.setTrimColor);
  const selectionByModel = useCustomizerStore((state) => state.selectionByModel);
  const setWheelIndex = useTransitionStore((state) => state.setWheelIndex);
  const trims = useMemo(() => fetchCustomizerData(), []);
  const [trimTrayOpen, setTrimTrayOpen] = useState(false);
  const [trimNotchHovered, setTrimNotchHovered] = useState(false);
  const trimNotchRef = useRef(null);
  const trimTrayRef = useRef(null);
  const trimTrayMaterialRef = useRef(null);
  const trim = trims[model];
  const colors = trim.exterior.colors;
  const wheelOptions = trim.wheels.colors ?? [];
  const wheelLabels = trim.wheels.text ?? [];
  const label = trim.label.toUpperCase();
  const hasWheelOptions = wheelOptions.length > 0;
  const colorRows = Math.ceil(colors.length / 3);
  const selectedColorY = XR_MENU_TOP - 0.17;
  const trimLabelY = selectedColorY - 0.14;
  const firstSwatchY = trimLabelY - 0.31;
  const lastSwatchY =
    firstSwatchY - Math.max(0, colorRows - 1) * XR_SWATCH_ROW_GAP;
  const firstWheelY = lastSwatchY - 0.28;
  const lastWheelY =
    firstWheelY - Math.max(0, wheelOptions.length - 1) * XR_WHEEL_ROW_GAP;
  const contentBottomY = hasWheelOptions ? lastWheelY - 0.11 : lastSwatchY - 0.14;
  const menuBottom = contentBottomY - XR_MENU_BOTTOM_PADDING;
  const menuHeight = XR_MENU_TOP - menuBottom;
  const menuCenterY = (XR_MENU_TOP + menuBottom) / 2;
  const selectedIndex = selectionByModel?.[model]?.exterior ?? 0;
  const selectedWheel = selectionByModel?.[model]?.wheel ?? 0;
  const selectedColorName =
    colorNamesExterior[selectedIndex]?.toUpperCase() ?? "";
  useEffect(() => {
    setWheelIndex(selectedWheel);
  }, [selectedWheel, setWheelIndex]);

  useXREvent(
    "squeezestart",
    () => {
      onOpenChange?.(!open);
    },
    { handedness: "left" }
  );

  const handleColorSelect = (color, index) => {
    setSelectedColorIndex(model, index, "exterior");
    setTrimColor(color, model);
  };

  const handleWheelSelect = (index) => {
    setSelectedWheelIndex(model, index);
    setWheelIndex(index);
  };

  const handleTrimSelect = (nextModel) => {
    if (nextModel === model) {
      return;
    }

    setModel(nextModel);
    setWheelIndex(getSelectedWheelIndex(nextModel));
  };

  const handleTrimTrayToggle = () => {
    setTrimTrayOpen((current) => !current);
  };

  useFrame((_, delta) => {
    const notch = trimNotchRef.current;
    const tray = trimTrayRef.current;
    const trayMaterial = trimTrayMaterialRef.current;

    if (!notch || !tray || !trayMaterial) {
      return;
    }

    const animationAlpha = 1 - Math.exp(-delta * 10);
    const targetNotchX = trimTrayOpen
      ? XR_TRIM_NOTCH_OPEN_X
      : XR_TRIM_NOTCH_CLOSED_X;
    const targetTrayScaleX = trimTrayOpen ? 1 : 0.08;
    const targetTrayOpacity = trimTrayOpen ? 0.94 : 0;

    notch.position.x = THREE.MathUtils.lerp(
      notch.position.x,
      targetNotchX,
      animationAlpha
    );
    tray.scale.x = THREE.MathUtils.lerp(
      tray.scale.x,
      targetTrayScaleX,
      animationAlpha
    );
    trayMaterial.opacity = THREE.MathUtils.lerp(
      trayMaterial.opacity,
      targetTrayOpacity,
      animationAlpha
    );
    tray.visible = tray.scale.x > 0.1 || trimTrayOpen;
  });

  if (!leftController) {
    return null;
  }

  return createPortal(
    <>
      <group
        visible={open}
        position={[0.1, 0.145, -0.135]}
        rotation={[-0.98, 0.18, -0.24]}
        scale={0.215}
      >
        <group position={[XR_TRIM_TRAY_X, 0, XR_TRIM_TRAY_Z]}>
          <Interactive
            onHover={() => setTrimNotchHovered(true)}
            onBlur={() => setTrimNotchHovered(false)}
            onSelect={handleTrimTrayToggle}
          >
            <group
              ref={trimNotchRef}
              position={[XR_TRIM_NOTCH_CLOSED_X, 0.08, 0.05]}
              scale={trimNotchHovered ? 1.05 : 1}
            >
              <mesh>
                <planeGeometry
                  args={[XR_TRIM_NOTCH_WIDTH, XR_TRIM_NOTCH_HEIGHT]}
                />
                <meshBasicMaterial
                  color="#eb0a1e"
                  transparent
                  opacity={0.98}
                />
              </mesh>
              <Text
                position={[0, 0, 0.02]}
                fontSize={0.074}
                maxWidth={0.12}
                color="#f5f3ef"
                anchorX="center"
                anchorY="middle"
              >
                {trimTrayOpen ? "<" : ">"}
              </Text>
            </group>
          </Interactive>

          <group
            ref={trimTrayRef}
            visible={false}
            position={[0, 0.08, 0.01]}
            scale={[0.08, 1, 1]}
          >
            <mesh>
              <planeGeometry args={[XR_TRIM_TRAY_WIDTH, XR_TRIM_TRAY_HEIGHT]} />
              <meshBasicMaterial
                ref={trimTrayMaterialRef}
                color="#0e1114"
                transparent
                opacity={0}
              />
            </mesh>
            {trims.map((trimOption, index) => (
              <TrimMenuOption
                key={`trim-selector-${trimOption.label}`}
                label={trimOption.label}
                isSelected={index === model}
                position={[
                  0,
                  XR_TRIM_TRAY_TOP_Y - index * XR_TRIM_TRAY_GAP,
                  0.03,
                ]}
                onSelect={() => handleTrimSelect(index)}
              />
            ))}
          </group>
        </group>

        <mesh position={[0, menuCenterY + 0.01, -0.01]}>
          <planeGeometry args={[XR_MENU_WIDTH, menuHeight]} />
          <meshBasicMaterial color="#0e1114" transparent opacity={0.92} />
        </mesh>

        <Text
          position={[0, selectedColorY, 0]}
          fontSize={0.08}
          maxWidth={1.18}
          color="#f5f3ef"
          anchorX="center"
          anchorY="middle"
        >
          {selectedColorName}
        </Text>
        <Text
          position={[0, trimLabelY, 0]}
          fontSize={0.05}
          maxWidth={1.18}
          color="#c7cbd1"
          anchorX="center"
          anchorY="middle"
        >
          {`${label} COLORS`}
        </Text>

        {colors.map((color, index) => {
          const column = index % 3;
          const row = Math.floor(index / 3);

          return (
            <ColorMenuSwatch
              key={`${label}-color-${index}`}
              color={color}
              isSelected={selectedIndex === index}
              position={[
                -0.42 + column * 0.42,
                firstSwatchY - row * XR_SWATCH_ROW_GAP,
                0,
              ]}
              onSelect={() => handleColorSelect(color, index)}
            />
          );
        })}

        {hasWheelOptions && (
          <>
            {wheelOptions.map((wheel, index) => (
              <WheelMenuOption
                key={`${label}-wheel-${index}`}
                index={index}
                isSelected={selectedWheel === index}
                label={wheelLabels[index] ?? `Option ${index + 1}`}
                wheelSrc={wheel}
                onSelect={() => handleWheelSelect(index)}
                position={[0, firstWheelY - index * XR_WHEEL_ROW_GAP, 0]}
              />
            ))}
          </>
        )}
      </group>

    </>,
    leftController.grip
  );
}

function TrimMenuOption({ label, isSelected, position, onSelect }) {
  const [hovered, setHovered] = useState(false);
  const scale = hovered ? 1.05 : 1;

  return (
    <Interactive
      onHover={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onSelect={onSelect}
    >
      <group position={position} scale={scale}>
        <mesh>
          <planeGeometry args={[0.4, 0.22]} />
          <meshBasicMaterial
            color={isSelected ? "#f5f3ef" : "#1d232a"}
            transparent
            opacity={isSelected ? 0.98 : hovered ? 0.94 : 0.84}
          />
        </mesh>
        <Text
          position={[0, 0, 0.02]}
          fontSize={0.052}
          maxWidth={0.3}
          color={isSelected ? "#0b0d0f" : "#f5f3ef"}
          anchorX="center"
          anchorY="middle"
        >
          {label.toUpperCase()}
        </Text>
      </group>
    </Interactive>
  );
}

function ColorMenuSwatch({ color, isSelected, onSelect, position }) {
  const [hovered, setHovered] = useState(false);
  const scale = hovered ? 1.05 : 1;
  const primaryColor = Array.isArray(color) ? color[0] : color;
  const secondaryColor = Array.isArray(color) ? color[1] : null;
  const isWhiteSwatch =
    !Array.isArray(color) && color.toLowerCase() === "#fffcf9";

  return (
    <Interactive
      onHover={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onSelect={onSelect}
    >
      <group position={position} scale={scale}>
        <mesh position={[0, 0.01, -0.006]}>
          <primitive object={SWATCH_OUTER_GEOMETRY} attach="geometry" />
          <meshBasicMaterial
            color={isSelected ? "#eb0a1e" : "#4b535d"}
            transparent
            opacity={isSelected ? 1 : hovered ? 0.92 : 0.75}
          />
        </mesh>

        <mesh position={[0, 0.01, -0.001]}>
          <primitive object={SWATCH_INNER_RING_GEOMETRY} attach="geometry" />
          <meshBasicMaterial color="#ffffff" transparent opacity={0.96} />
        </mesh>

        <mesh position={[0, 0.01, 0.002]} scale={SWATCH_FILL_SCALE}>
          <primitive object={SWATCH_FILL_GEOMETRY} attach="geometry" />
          <meshBasicMaterial color={primaryColor} />
        </mesh>

        {secondaryColor && (
          <mesh position={[0, 0.01, 0.004]} scale={SWATCH_FILL_SCALE}>
            <primitive object={SWATCH_HALF_FILL_GEOMETRY} attach="geometry" />
            <meshBasicMaterial color={secondaryColor} side={THREE.DoubleSide} />
          </mesh>
        )}

        {isWhiteSwatch && (
          <mesh position={[0, 0.01, 0.006]} scale={SWATCH_FILL_SCALE}>
            <primitive object={SWATCH_WHITE_EDGE_GEOMETRY} attach="geometry" />
            <meshBasicMaterial color="#d6d6d6" transparent opacity={0.85} />
          </mesh>
        )}
      </group>
    </Interactive>
  );
}

function WheelMenuOption({
  index,
  isSelected,
  label,
  wheelSrc,
  onSelect,
  position,
}) {
  const wheelTexture = useTexture(wheelSrc);
  const [hovered, setHovered] = useState(false);
  const scale = hovered ? 1.05 : 1;

  return (
    <Interactive
      onHover={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onSelect={onSelect}
    >
      <group position={position} scale={scale}>
        <mesh>
          <planeGeometry args={[1.02, 0.22]} />
          <meshBasicMaterial
            color={isSelected ? "#ffffff" : "#222a31"}
            transparent
            opacity={isSelected ? 0.95 : 0.84}
          />
        </mesh>
        <mesh position={[-0.33, 0, 0.02]}>
          <circleGeometry args={[0.1, 40]} />
          <meshBasicMaterial map={wheelTexture} transparent />
        </mesh>
        <Text
          position={[0.02, 0, 0.02]}
          fontSize={0.054}
          maxWidth={0.78}
          color={isSelected ? "#0b0d0f" : "#f5f3ef"}
          anchorX="left"
          anchorY="middle"
        >
          {label.toUpperCase()}
        </Text>
      </group>
    </Interactive>
  );
}

const StageRoot = styled.section`
  position: relative;
  height: 100vh;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  padding: 112px 32px 36px;
  pointer-events: none;

  .meta,
  .actions {
    pointer-events: auto;
  }

  .meta {
    max-width: 430px;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .actions {
    position: absolute;
    top: calc((var(--app-header-height, 104px) - 52px) / 2);
    right: 32px;
    display: flex;
    justify-content: flex-end;
  }

  .eyebrow {
    display: block;
    margin-bottom: 10px;
    font-size: 0.72rem;
    letter-spacing: 0.26em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.62);
  }

  h2 {
    margin: 0 0 10px;
    font-size: clamp(2rem, 4vw, 3.4rem);
    line-height: 0.96;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.78);
  }

  button {
    height: 52px;
    border-radius: 999px;
    border: 0;
    padding: 0 24px;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    cursor: pointer;
  }

  .primary {
    background: #f5f3ef;
    color: #050505;
  }

  .ghost {
    width: fit-content;
    background: rgba(255, 255, 255, 0.08);
    color: #f5f3ef;
    border: 1px solid rgba(255, 255, 255, 0.14);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    justify-content: space-between;
    padding: 116px 18px 24px;

    .actions {
      top: calc((var(--app-header-height, 60px) - 44px) / 2);
      right: 18px;
    }

    .actions button,
    .ghost {
      width: auto;
    }

    button {
      height: 44px;
      padding: 0 20px;
      font-size: 0.8rem;
    }
  }
`;

const UnsupportedVrModal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 12000;
  pointer-events: auto;

  .backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.68);
    backdrop-filter: blur(10px);
  }

  .panel {
    position: absolute;
    left: 50%;
    top: 50%;
    width: min(520px, calc(100vw - 32px));
    padding: 28px 28px 24px;
    border-radius: 28px;
    transform: translate(-50%, -50%);
    background: rgba(8, 8, 10, 0.96);
    box-shadow:
      0 28px 80px rgba(0, 0, 0, 0.42),
      inset 0 0 0 1px rgba(255, 255, 255, 0.08);
    color: #f5f3ef;
  }

  .eyebrow {
    display: block;
    margin-bottom: 12px;
    color: rgba(255, 255, 255, 0.54);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.24em;
    text-transform: uppercase;
  }

  h3 {
    margin: 0 0 14px;
    font-size: clamp(1.7rem, 4vw, 2.5rem);
    line-height: 0.98;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.78);
    font-size: 1rem;
    line-height: 1.5;
  }

  .dismiss {
    margin-top: 22px;
    height: 48px;
    border: 0;
    border-radius: 999px;
    padding: 0 22px;
    background: #ffffff;
    color: #050505;
    font-size: 0.84rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    cursor: pointer;
  }

  @media (max-width: 768px) {
    .panel {
      padding: 22px 18px 18px;
    }

    .dismiss {
      width: 100%;
    }
  }
`;
